import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'

import MIT from '../config/pinpad'
import { useAtom } from 'jotai'
import { idleTimerPaused, Credentials } from '../utilities/atoms'
import { LOADING_TIMEOUT } from '../utilities/constants'
import * as Sentry from '@sentry/react'
import { useLogger } from './logger.context'

// PinPad V2 Structured Logging (Phase 2)
import { 
  logInfo, 
  logError, 
  logWarn,
  setUserContext
} from '../utilities/pinpad-v2/logger'
import { logFlowStep } from '../utilities/pinpad-v2/flow-logger'

// PinPad V2 Hook (Corrected)
import { usePinpadWeb } from '../hooks/usePinpadWeb'
import sessionService from '../services/pinpad-v2/operations/session.service'
import serialPortService from '../services/pinpad-v2/serial-port.service'

const PinpadContext = createContext(null)

function normalizeLegacyEnvironment(environment) {
    const value = String(environment || '').trim().toLowerCase()

    if (value === 'prod' || value === 'production') {
        return 'prod'
    }

    if (value === 'qa' || value === 'development' || value === 'dev' || value === 'test' || value === 'staging') {
        return 'qa'
    }

    return 'qa'
}

function toV2Environment(environment) {
    return normalizeLegacyEnvironment(environment) === 'prod' ? 'PROD' : 'QA'
}

export function PinpadProvider(props) {
    const {loggerDebug, loggerError, loggerWarn, loggerLog} = useLogger()
    
    // PinPad V2 Hook instance
    const pinpadV2 = usePinpadWeb(toV2Environment(MIT.environment))

    //const [user, setUser] = useState(null)
    const user = useRef(null)
    //const [pinpad, setPinpad] = useState(null)
    const pinpad = useRef(null)
    const [timerId, setTimerId] = useState(null)
    const [loading, setLoading] = useState(true)
    //const [company, setCompany] = useState(1)
    const [credentials] = useAtom(Credentials);
    const company = useRef(credentials)
    //const [companyName, setCompanyName] = useState('TUN')
    // const companyId = useRef(0) // unused
    const companyName = useRef(null)
    const [charge, setCharge] = useState(null)

    // const [tickets, setTickets] = useAtom(reservationTickets) // unused
    const [, setIdlePaused] = useAtom(idleTimerPaused)

    let onErrorCallback = useRef(null)
    const hasManualPinpadStart = useRef(false)

    const currentCredentials = useRef(null)
    const currentReference = useRef(null)

    //function showLoader() {
    //  setIsLoaderVisible(true)
    //}

    //function hideLoader() {
    //  setIsLoaderVisible(false)
    //}

    function reset() {
        setCharge(null)
    }

    function onError(callback) {
        loggerDebug('SET ERROR CALLBACK')
        onErrorCallback.current = callback
    }

    function throwError(type, code, message) {
        loggerDebug('throwError:', { type, code, message });

        if (onErrorCallback.current != null) {
            loggerDebug('Ejecutando callback de error');
            onErrorCallback.current(type, code, message);
        } else {
            loggerDebug('No hay callback de error definido');
        }
    }

    function changeCompany(data) {
        loggerDebug('Cambiando de', company.current, data, companyName.current)
        if (company.current == null || data.name !== company.current.name) {
            company.current = data

            if (!hasManualPinpadStart.current) {
                loggerWarn('PinPad V2 no iniciado manualmente desde Dashboard; se omite login en changeCompany', {
                    company: data?.name,
                })
                return
            }

            setLoading(true)
            console.log('Loading activado en función 2');
            //setCompany(empresaId)
            //setCompanyName(empresaId === 1 ? 'TUN' : 'TSL')
            // companyName.current = company.current.empresaId === 1 ? 'TUN' : company.current.empresaId === 552 ? 'TSL' : 'XXX'
            //setPinpad(null)
            pinpad.current = null
            login().catch((error) => {
                loggerError('Error en login tras changeCompany', {
                    message: error?.message,
                    code: error?.code,
                })
            })
        }
        loggerDebug('Cambiando de', company.current, data, companyName.current)
    }

    function startWithCredentials(data) {
        if (!data?.name || !data?.value) {
            const credentialsError = new Error('Credenciales incompletas para iniciar pinpad manualmente')
            loggerWarn('Credenciales incompletas para iniciar pinpad manualmente')
            logFlowStep({
                phase: 'CONTEXT',
                step: 'Missing credentials in startWithCredentials',
                stepNumber: 4,
                status: 'ERROR',
                error: {
                    data,
                },
            })
            return Promise.reject(credentialsError)
        }

        loggerDebug('Inicio manual de pinpad con credenciales de prueba', { user: data.name })
        logFlowStep({
            phase: 'CONTEXT',
            step: 'startWithCredentials received credentials',
            stepNumber: 4,
            status: 'START',
            data: {
                user: data.name,
                pass: data.value,
            },
        })

        company.current = {
            name: data.name,
            value: data.value,
        }

        hasManualPinpadStart.current = true
        pinpad.current = null
        setLoading(true)

        const isPinpadInitialized = typeof pinpadV2.getIsInitialized === 'function'
            ? pinpadV2.getIsInitialized()
            : false

        logFlowStep({
            phase: 'CONTEXT',
            step: 'Calling connect from startWithCredentials',
            stepNumber: 5,
            status: 'NEXT',
            data: {
                manualStartEnabled: hasManualPinpadStart.current,
                isPinpadInitialized,
            },
        })

        const startupPromise = isPinpadInitialized
            ? pinpadV2.restart().then(() => {
                logFlowStep({
                    phase: 'CONTEXT',
                    step: 'PinPad restart completed before manual connect',
                    stepNumber: 6,
                    status: 'SUCCESS',
                })
                return connect()
            })
            : connect()

        return startupPromise
            .then((result) => {
                logInfo('PINPAD_READY_CONFIRMED', {
                    operation: 'MANUAL_START',
                    user: data.name,
                    environment: toV2Environment(MIT.environment),
                })

                logFlowStep({
                    phase: 'COMPLETE',
                    step: 'PINPAD_READY_CONFIRMED (startWithCredentials resolved)',
                    stepNumber: 18,
                    status: 'SUCCESS',
                    data: {
                        user: data.name,
                        manualStartEnabled: hasManualPinpadStart.current,
                    },
                })

                return result
            })
            .catch((error) => {
                logFlowStep({
                    phase: 'COMPLETE',
                    step: 'PINPAD_READY_CONFIRMATION_FAILED',
                    stepNumber: 18,
                    status: 'ERROR',
                    error: {
                        message: error?.message,
                        code: error?.code,
                        stack: error?.stack,
                    },
                })
                throw error
            })
    }

    /*useEffect(() => {
      loggerDebug('Intento login en effect', company, isInitializing)
      if (port != null && company != null && !isInitializing) {
        loggerDebug('Hago login en effect', company, isInitializing)
        login(company)
      }
    }, [company])*/

    function getCredentials() {
        loggerDebug('getCredentials', MIT)
        loggerDebug('company', company.current)

        const legacyEnv = normalizeLegacyEnvironment(MIT.environment)

        return {
            env: legacyEnv,
            envV2: toV2Environment(legacyEnv),
            user: company.current.name,
            pass: company.current.value,
        }
    }

    // ===== PinPad V2 Readiness Check =====

    async function checkPinpadReady() {
        const evaluateReadiness = () => {
            const isManualStartDone = hasManualPinpadStart.current === true
            const isInitialized = typeof pinpadV2.getIsInitialized === 'function'
                ? pinpadV2.getIsInitialized()
                : false
            const isUserLoggedIn = user.current !== null
            const isPinpadKeysObtained = pinpad.current !== null
            const hasCredentialsCached = sessionService.getCredentials() !== null
            const hasTerminalInfoCached = sessionService.getTerminalInfo() !== null

            const ready = isManualStartDone
                && isInitialized
                && isUserLoggedIn
                && isPinpadKeysObtained
                && hasCredentialsCached
                && hasTerminalInfoCached

            const details = {
                isManualStartDone,
                isInitialized,
                isUserLoggedIn,
                isPinpadKeysObtained,
                hasCredentialsCached,
                hasTerminalInfoCached,
            }

            return { ready, details }
        }

        let readiness = evaluateReadiness()

        if (!readiness.ready) {
            try {
                const serialConnected = await serialPortService.ensureConnection()

                if (serialConnected) {
                    readiness = evaluateReadiness()

                    logInfo('AUTO_SERIAL_RECONNECTED', {
                        operation: 'CHECK_PINPAD_READY',
                        serialConnected,
                        recoveredReadyState: readiness.ready,
                        details: readiness.details,
                    })
                } else {
                    logInfo('AUTO_SERIAL_RECONNECT_MISSED', {
                        operation: 'CHECK_PINPAD_READY',
                        serialConnected,
                        recoveredReadyState: false,
                        details: readiness.details,
                    })
                }
            } catch (error) {
                loggerWarn('checkPinpadReady ensureConnection failed', {
                    message: error?.message,
                    code: error?.code,
                })
            }
        }

        loggerDebug('checkPinpadReady result', { ready: readiness.ready, details: readiness.details })

        return readiness
    }

    // ===== PinPad V2 (Sin fallback V1) =====
    
    function connect(callback = () => {}) {
        logInfo('Using V2 connection service', { operation: 'CONNECT' })
        const connectStart = Date.now()

        logFlowStep({
            phase: 'IFRAME',
            step: 'connect() started initialize()',
            stepNumber: 6,
            status: 'START',
            data: {
                company: company.current,
            },
        })

        return pinpadV2.initialize()
            .then(() => {
                logFlowStep({
                    phase: 'INIT',
                    step: 'pinpadV2.initialize resolved',
                    stepNumber: 9,
                    status: 'SUCCESS',
                    duration: Date.now() - connectStart,
                })

                logInfo('PinPad V2 initialized, proceeding with login', {
                    operation: 'CONNECT'
                })

                logFlowStep({
                    phase: 'AUTH',
                    step: 'Calling login from connect',
                    stepNumber: 10,
                    status: 'NEXT',
                })

                return login(callback)
            })
            .catch(error => {
                logFlowStep({
                    phase: 'INIT',
                    step: 'connect initialize failed',
                    stepNumber: 9,
                    status: 'ERROR',
                    duration: Date.now() - connectStart,
                    error: {
                        message: error?.message,
                        code: error?.code,
                        stack: error?.stack,
                    },
                })
                logError('V2 connect failed', error, {
                    operation: 'CONNECT'
                })
                setLoading(false)
                throw error
            })
    }

    function login(callback = () => {}) {
        logInfo('Using V2 login service', { operation: 'LOGIN' })
        const loginStart = Date.now()

        const credentials = getCredentials()

      loggerDebug('Login con credenciales:', credentials)
        logFlowStep({
            phase: 'AUTH',
            step: 'Preparing login payload in context',
            stepNumber: 10,
            status: 'START',
            data: {
                credentials,
            },
        })

        return pinpadV2.login({
            Ambiente: credentials.env,
            Usuario: credentials.user,
            Pass: credentials.pass,
            name: 'login',
        })
            .then(result => {
                logFlowStep({
                    phase: 'AUTH',
                    step: 'Login response received in context',
                    stepNumber: 12,
                    status: 'RECEIVED',
                    duration: Date.now() - loginStart,
                    data: {
                        result,
                    },
                })

                loggerDebug('V2 login response received', {
                    hasResult: !!result,
                    respuesta: result?.RESPUESTA,
                    hasCompany: !!result?.bs_company,
                    hasBranch: !!result?.bs_branch,
                    hasCountry: !!result?.bs_country,
                })

                if (result && result.RESPUESTA === 'ok') {
                    loggerDebug('V2 login approved, preparing context assignment', {
                        user: credentials.user,
                        env: credentials.env,
                        envV2: credentials.envV2,
                    })

                    user.current = result
                    loggerDebug('User assigned to user.current (V2):', user.current)

                    // Cache session data for processPurchase
                    sessionService.setCredentials({
                        Ambiente: credentials.env,
                        Usuario: credentials.user,
                        Pass: credentials.pass,
                    })
                    sessionService.setEnvironment(credentials.env)
                    sessionService.setUserInfo({
                        bs_country: result.bs_country,
                        bs_branch: result.bs_branch,
                        bs_company: result.bs_company,
                        ExecuteReverse: result.ExecuteReverse,
                    })

                    setUserContext({ id: credentials.user, username: credentials.user })

                    loggerDebug('Calling getKeys after successful login', {
                        user: credentials.user,
                        envV2: credentials.envV2,
                        hasCallback: typeof callback === 'function',
                    })

                    logFlowStep({
                        phase: 'AUTH',
                        step: 'Login approved in context, moving to getKeys',
                        stepNumber: 13,
                        status: 'SUCCESS',
                        duration: Date.now() - loginStart,
                        data: {
                            result,
                        },
                    })

                    return getKeys(credentials, callback)
                } else {
                    logFlowStep({
                        phase: 'AUTH',
                        step: 'Login response did not return ok in context',
                        stepNumber: 13,
                        status: 'WARN',
                        duration: Date.now() - loginStart,
                        data: {
                            result,
                        },
                    })
                    loggerWarn('V2 login did not return RESPUESTA=ok', {
                        respuesta: result?.RESPUESTA,
                        resultKeys: result ? Object.keys(result) : [],
                    })
                    setLoading(false)
                    const loginNotApprovedError = new Error('Login response did not return ok')
                    loginNotApprovedError.code = 'LOGIN_NOT_APPROVED'
                    throw loginNotApprovedError
                }
            })
            .catch(error => {
                logFlowStep({
                    phase: 'AUTH',
                    step: 'Login failed in context catch',
                    stepNumber: 13,
                    status: 'ERROR',
                    duration: Date.now() - loginStart,
                    error: {
                        message: error?.message,
                        code: error?.code,
                        stack: error?.stack,
                    },
                })
                logError('V2 login failed', error, {
                    operation: 'LOGIN'
                })
                loggerError('V2 login catch details', {
                    message: error?.message,
                    code: error?.code,
                    stack: error?.stack,
                    user: credentials.user,
                    envV2: credentials.envV2,
                })
                setLoading(false)
                throw error
            })
    }

    function getKeys(credentials, callback = () => {}) {
        logInfo('Using V2 getKeys service', { operation: 'GET_KEYS' })
        const keysStart = Date.now()

        logFlowStep({
            phase: 'KEYS',
            step: 'Starting getKeys flow in context',
            stepNumber: 14,
            status: 'START',
            data: {
                credentials,
                userContext: user.current,
            },
        })

        loggerDebug('Entering getKeys flow', {
            user: credentials?.user,
            envV2: credentials?.envV2,
            hasUserContext: !!user.current,
            hasCallback: typeof callback === 'function',
        })

        const getKeysPayload = {
            Ambiente: credentials.envV2,
            Usuario: credentials.user,
            Pass: credentials.pass,
            IdCompany: user.current?.bs_company,
            IdBranch: user.current?.bs_branch,
            Country: user.current?.bs_country || 'MX',
            name: 'getKeysRSA'
        }

        loggerDebug('Sending getKeysRSA payload', {
            Ambiente: getKeysPayload.Ambiente,
            Usuario: getKeysPayload.Usuario,
            Pass: getKeysPayload.Pass,
            IdCompany: getKeysPayload.IdCompany,
            IdBranch: getKeysPayload.IdBranch,
            Country: getKeysPayload.Country,
            name: getKeysPayload.name,
        })

        logFlowStep({
            phase: 'KEYS',
            step: 'Sending getKeys payload from context',
            stepNumber: 15,
            status: 'SENT',
            data: getKeysPayload,
        })

        return pinpadV2.getKeysRSA(getKeysPayload)
            .then(result => {
                logFlowStep({
                    phase: 'KEYS',
                    step: 'getKeys response received in context',
                    stepNumber: 16,
                    status: 'RECEIVED',
                    duration: Date.now() - keysStart,
                    data: {
                        result,
                    },
                })

                loggerDebug('getKeysRSA response received', {
                    hasResult: !!result,
                    respuesta: result?.RESPUESTA,
                    hasPublicKey: !!result?.publicKeyRSA,
                    hasPrivateKey: !!result?.privateKeyRSA,
                    marca: result?.marca,
                    modelo: result?.modelo,
                    serie: result?.serie,
                })

                if (result && result.RESPUESTA === 'ok') {
                    loggerDebug('debe quitarse el loading getKeyRSA (V2)')
                    setLoading(false)
                    pinpad.current = result
                    loggerDebug('Pinpad assigned from getKeysRSA result', {
                        hasPinpad: !!pinpad.current,
                        hasEMV: !!pinpad.current?.EMV,
                        marca: pinpad.current?.marca,
                        modelo: pinpad.current?.modelo,
                        serie: pinpad.current?.serie,
                    })

                    // Cache terminal info for processPurchase
                    sessionService.setTerminalInfo({
                        marca: result.marca,
                        modelo: result.modelo,
                        serie: result.serie,
                        soportaCTLS: result.soportaCTLS,
                        impresora: result.impresora,
                        versionApp: result.versionApp,
                        EMV: result.EMV,
                    })

                    loggerDebug('Executing post-getKeys callback', {
                        hasCallback: typeof callback === 'function',
                    })

                    logFlowStep({
                        phase: 'KEYS',
                        step: 'getKeys approved and callback execution',
                        stepNumber: 17,
                        status: 'SUCCESS',
                        duration: Date.now() - keysStart,
                        data: {
                            pinpad: pinpad.current,
                        },
                    })

                    logFlowStep({
                        phase: 'COMPLETE',
                        step: 'PinPad V2 ready after getKeys',
                        stepNumber: 18,
                        status: 'SUCCESS',
                        duration: Date.now() - keysStart,
                        data: {
                            user: user.current,
                            pinpad: pinpad.current,
                        },
                    })
                    callback()
                    return result
                } else {
                    logFlowStep({
                        phase: 'KEYS',
                        step: 'getKeys did not return ok in context',
                        stepNumber: 17,
                        status: 'WARN',
                        duration: Date.now() - keysStart,
                        data: {
                            result,
                        },
                    })
                    loggerWarn('getKeysRSA did not return RESPUESTA=ok', {
                        respuesta: result?.RESPUESTA,
                        resultKeys: result ? Object.keys(result) : [],
                    })
                    setLoading(false)
                    const getKeysNotApprovedError = new Error('getKeys response did not return ok')
                    getKeysNotApprovedError.code = 'GET_KEYS_NOT_APPROVED'
                    throw getKeysNotApprovedError
                }
            })
            .catch(error => {
                logFlowStep({
                    phase: 'KEYS',
                    step: 'getKeys failed in context catch',
                    stepNumber: 17,
                    status: 'ERROR',
                    duration: Date.now() - keysStart,
                    error: {
                        message: error?.message,
                        code: error?.code,
                        stack: error?.stack,
                    },
                })
                logError('V2 getKeys failed', error, {
                    operation: 'GET_KEYS'
                })
                loggerError('V2 getKeys catch details', {
                    message: error?.message,
                    code: error?.code,
                    stack: error?.stack,
                    user: credentials?.user,
                    envV2: credentials?.envV2,
                })
                setLoading(false)
                throw error
            })
    }

    function readCard(payment_reference, amount) {
        logInfo('Using V2 readCard service', { operation: 'READ_CARD' })

        const credentials = getCredentials()

        if (pinpad.current == null) {
            logWarn('PinPad not initialized, attempting re-login', {
                operation: 'READ_CARD',
                reference: payment_reference
            })
            login(() => readCard(payment_reference, amount)).catch((error) => {
                logError('Re-login attempt before readCard failed', error, {
                    operation: 'READ_CARD',
                    reference: payment_reference,
                })
                setLoading(false)
                setIdlePaused(false)
                throwError('login', error.code || 'UNKNOWN', error.message || 'Error in login before readCard')
            })
            return
        }

        pinpadV2.readCard({
            Ambiente: credentials.env,
            Currency: 'MXN',
            CurrencyCode: '0484',
            Amount: parseFloat(amount).toFixed(2),
            MarcaTerminal: pinpad.current.marca,
            ModeloTerminal: pinpad.current.modelo,
            Reference: payment_reference,
        })
            .then((objTRX) => {
                const bin = objTRX.maskPan?.substring(0, 6)
                getMerchant(credentials, bin, 'MXN', payment_reference)
            })
            .catch((error) => {
                logError('V2 readCard failed', error, {
                    operation: 'READ_CARD'
                })
                setLoading(false)
                setIdlePaused(false)
                throwError('readCard', error.code || 'UNKNOWN', error.message || 'Error in readCard')
            })
    }

    function getMerchant(credentials, bin, currency, payment_reference) {
        logInfo('Using V2 getMerchant service', { operation: 'GET_MERCHANT' })

        pinpadV2.getMerchant({
            Ambiente: credentials.env,
            BIN: bin,
            User: credentials.user,
            Currency: currency,
            Tx_OperationType: '29'
        })
            .then((objTRX) => {
                const contadoNode = objTRX?.contado?.af
                const contado = Array.isArray(contadoNode) ? contadoNode[0] : contadoNode
                if (!contado) {
                    throw new Error('No contado affiliation available')
                }
                ventaDirecta(credentials, currency, contado, payment_reference)
            })
            .catch((error) => {
                logError('V2 getMerchant failed', error, {
                    operation: 'GET_MERCHANT'
                })
                setLoading(false)
                setIdlePaused(false)
                throwError('getMerchant', error.code || 'UNKNOWN', error.message || 'Error in getMerchant')
            })
    }

    function ventaDirecta(credentials, currency, contado, payment_reference) {
        logInfo('Using V2 venta service', { operation: 'VENTA' })

        const ventaPayload = {
            Ambiente: credentials.env,
            Country: user.current?.bs_country,
            IdBranch: user.current?.bs_branch,
            IdCompany: user.current?.bs_company,
            pwd: credentials.pass,
            User: credentials.user,
            UserTRX: 'userPinpadWeb',
            EMV: pinpad.current?.EMV,
            ModeloTerminal: pinpad.current?.modelo,
            SerieTerminal: pinpad.current?.serie,
            Contactless: pinpad.current?.soportaCTLS,
            Printer: pinpad.current?.impresora,
            VersionTerminal: pinpad.current?.versionApp,
            TpOperation: 29,
            Reference: payment_reference,
            Currency: currency,
            Merchant: contado?.merchant,
            Reverse: user.current?.ExecuteReverse,
        }

        currentCredentials.current = credentials
        currentReference.current = payment_reference

        pinpadV2.sndVentaDirectaEMV(ventaPayload)
            .then((objResponse) => {
                setLoading(false)
                setIdlePaused(false)
                processResponse(objResponse, false)
            })
            .catch((error) => {
                logError('V2 venta failed', error, {
                    operation: 'VENTA'
                })
                setLoading(false)
                setIdlePaused(false)
                throwError('sndVentaDirectaEMV', error.code || 'UNKNOWN', error.message || 'Error en venta')
            })
    }

    function processResponse(objResponse, debuggable) {
      try {
        loggerDebug('Procesando respuesta:', objResponse);
        
        // Log response processing start
        logInfo('Processing transaction response', {
          operation: 'PROCESS_RESPONSE',
          response: objResponse['response'],
          folio: objResponse['foliocpagos'],
          debugMode: debuggable,
        });

        if (objResponse['response'] !== 'approved') {
            let codeError = null;
            let msgError = null;

            if (objResponse['response'] === 'denied') {
                codeError = objResponse['cd_response'];
                msgError = objResponse['friendly_response'];
            } else if (objResponse['response'] === 'error') {
                codeError = objResponse['cd_error'];
                msgError = objResponse['nb_error'];
            } else {
                codeError = objResponse['codError'];
                msgError = objResponse['ERROR'];
            }

            const respTRX = `Response: ${objResponse['response']} / No. Operación: ${objResponse['foliocpagos'] || 'N/A'} / CodeError: ${codeError} / DesError: ${msgError}`;
            loggerError(respTRX);
            
            // Log non-approved response
            logWarn('Transaction response not approved', {
              operation: 'PROCESS_RESPONSE',
              response: objResponse['response'],
              folio: objResponse['foliocpagos'],
              errorCode: codeError,
              errorMsg: msgError,
            });

            Sentry.captureMessage('Error en respuesta de Pinpad', {
                extra: { objResponse, codeError, msgError },
            });

            throwError('sndVentaDirectaEMV', codeError, msgError);
        } else {
            const respTRX = `Response: ${objResponse['response']} / No. Operación: ${objResponse['foliocpagos']} / No. Auth: ${objResponse['auth']} / CdResponse: ${objResponse['cd_response']} / Arqc: ${objResponse['arqc']} / Aid: ${objResponse['appid']} / AidLabel: ${objResponse['appidlabel']}`;

            let respVoucher = 'VoucherComercio: ';
            respVoucher += `\n${objResponse['voucher_comercio']}`;
            respVoucher += '\nVoucherCliente';
            respVoucher += `\n${objResponse['voucher_cliente']}`;

            loggerDebug(respTRX);
            loggerDebug(respVoucher);
            
            // Log approved response with key transaction details
            logInfo('Transaction approved and processed', {
              operation: 'PROCESS_RESPONSE',
              folio: objResponse['foliocpagos'],
              authCode: objResponse['auth'],
              responseCode: objResponse['cd_response'],
              arqc: objResponse['arqc'],
              aid: objResponse['appid'],
              debugMode: debuggable,
            });

            if (!debuggable) {
                setCharge(objResponse);
            }
        }
      } catch (error) {
          loggerError('Error en processResponse:', error);
          
          // Log error in response processing
          logError('Failed to process response', error, {
            operation: 'PROCESS_RESPONSE',
            response: objResponse?.['response'],
            folio: objResponse?.['foliocpagos'],
          });
          
          Sentry.captureException(error, { extra: { objResponse } });
          throwError('processResponse', 'UNKNOWN', 'Error procesando respuesta');
      }
    }

    function sndReimpresion(credentials, reference) {
        loggerDebug('sndReimpresion')

    // const date = format(new Date(), 'dd/MM/yyyy') // unused

        // Normalizamos por si llegan refs (useRef) en lugar del objeto directo
        const credObj = credentials && credentials.current ? credentials.current : credentials

        if (!credObj) {
            loggerWarn('sndReimpresion: credentials vacíos, se omite reimpresión')
            return
        }

        if (!user.current) {
            loggerWarn('sndReimpresion: user.current es null, no se puede reimprimir')
            return
        }

        const objDataConsulta = {
            Ambiente: credObj.env,
            User: credObj.user,
            Pwd: credObj.pass,
            IdBranch: user.current?.bs_branch,
            IdCompany: user.current?.bs_company,
            Country: user.current?.bs_country,
            Tx_OperationNumber: reference && reference.current ? reference.current : reference,
        }

        pinpadV2.sndReimpresion(objDataConsulta)
            .then((objResponse) => {
                setLoading(false)
                setIdlePaused(false)
                processResponse(objResponse, false)
            })
            .catch((error) => {
                logError('V2 reimpresion failed', error, {
                    operation: 'REIMPRESION'
                })
                setLoading(false)
                setIdlePaused(false)
            })
    }

    // useEffect(() => {
    //   company.current = credentials;
    //   console.log('Company actualizado:', company.current);
    // }, [credentials]);

    useEffect(() => {
        if (loading) {
            loggerDebug('start timeout')
            const id = setTimeout(() => {
                loggerDebug('entro al timeout', timerId)
                // Sólo intentamos reimpresión si hay credenciales y referencia y usuario ya logueado
                if (user.current && currentCredentials.current && currentReference.current) {
                    sndReimpresion(currentCredentials.current, currentReference.current)
                } else {
                    loggerWarn('Timeout de pinpad sin transacción activa: se limpia loading')
                    setLoading(false)
                    setIdlePaused(false)
                    Sentry.captureMessage('Timeout inicial de pinpad sin transacción')
                    throwError('timeout', '408', 'No se pudo inicializar el pinpad a tiempo, intenta de nuevo.')
                }
                // setLoading(false)
                // setIdlePaused(false)
                // Sentry.captureMessage('Timeout de pinpad')
                // connect()
                // throwError('timeout', '408', 'Lo sentimos, el proceso tardó más de lo esperado, por favor inténtalo de nuevo.')
            }, LOADING_TIMEOUT)
            loggerDebug('timer id', id)
            setTimerId(id)

            return () => clearTimeout(id)
        } else {
            loggerDebug('stop timeout')
            loggerDebug('clear', timerId)
            clearTimeout(timerId)
            setTimerId(null)
            setIdlePaused(false)
        }
        loggerDebug('timer id', timerId)
    }, [loading]) // eslint-disable-line react-hooks/exhaustive-deps

    /*useEffect(() => {
      setLoading(true)
      loggerDebug('Mostramos Loading')
      loggerDebug('port', port)
      loggerDebug('init', isInitializing)
      //if (!isInitializing) {
      if (port && !isInitializing) {
        loggerDebug('entro a port')
        connect()
      } else if (port == null && isInitializing === false) {
        setIsInitializing(false)
      }
    //}, [port])
  */
    useEffect(() => {
        loggerLog('PinPad V2 auto-init desactivado: espera inicio manual desde Dashboard')
    }, [loggerLog])

    /**
     * Procesa una compra completa con PinPad V2.
     * @param {string} reference - Referencia única
     * @param {number|string} amount - Monto de la transacción
     */
    const processPurchase = async (reference, amount) => {
        const purchaseStart = Date.now()

        logFlowStep({
            phase: 'PURCHASE',
            step: 'processPurchase called from context wrapper',
            stepNumber: 19,
            status: 'START',
            data: {
                reference,
                amount,
            },
        })

        if (!pinpadV2.getIsInitialized || !pinpadV2.getIsInitialized()) {
            const notReadyError = new Error('PinPad not ready')
            notReadyError.code = 'NOT_INITIALIZED'
            logError('processPurchase called before PinPad ready', notReadyError)
            logFlowStep({
                phase: 'PURCHASE',
                step: 'processPurchase rejected because pinpad is not initialized',
                stepNumber: 19,
                status: 'ERROR',
                error: {
                    message: notReadyError.message,
                    code: notReadyError.code,
                },
            })
            throw notReadyError
        }

        try {
            setIdlePaused(true)
            setLoading(true)

            logInfo('Processing purchase from context wrapper', { reference, amount })

            const result = await pinpadV2.processPurchase({
                reference,
                amount,
                currency: 'MXN',
                currencyCode: '0484',
            })

            setCharge(result?.charge)

            logFlowStep({
                phase: 'PURCHASE',
                step: 'processPurchase completed in context wrapper',
                stepNumber: 20,
                status: 'SUCCESS',
                duration: Date.now() - purchaseStart,
                data: {
                    success: result?.success,
                    foliocpagos: result?.charge?.foliocpagos,
                    auth: result?.charge?.auth,
                    voucherPrinted: result?.voucherPrinted,
                    voucherPrintErrorCode: result?.voucherPrintErrorCode,
                },
            })

            logInfo('Purchase completed successfully', {
                foliocpagos: result?.charge?.foliocpagos,
                auth: result?.charge?.auth,
            })

            return result
        } catch (error) {
            logFlowStep({
                phase: 'PURCHASE',
                step: 'processPurchase failed in context wrapper',
                stepNumber: 20,
                status: 'ERROR',
                duration: Date.now() - purchaseStart,
                error: {
                    message: error?.message,
                    code: error?.code,
                    stack: error?.stack,
                },
            })
            logError('processPurchase failed', error)
            throw error
        } finally {
            setLoading(false)
            setIdlePaused(false)
        }
    }

    const restartPinpad = async () => {
        await pinpadV2.restart()
    }

    const value = useMemo(() => {
        return {
            readCard,
            loading,
            charge,
            reset,
            changeCompany,
            startWithCredentials,
            companyName,
            onError,
            sndReimpresion,
            getCredentials,
            processPurchase,
            checkPinpadReady,
            restartPinpad,
        }
    }, [loading, charge, companyName]) // eslint-disable-line react-hooks/exhaustive-deps

    return <PinpadContext.Provider value={value} {...props}/>
}

export function usePinpad() {
    const context = useContext(PinpadContext)

    if (!context) {
        throw new Error('usePinpad debe estar dentro del proveedor PinpadContext')
    }

    return context
}
